import { useState } from 'react'
import { pdf } from '@react-pdf/renderer'
import { useAuth } from '@/features/auth/AuthProvider'
import { useDepositos } from '@/features/productos/hooks'
import { useCajaActual } from '@/features/caja/hooks'
import { useVentas, useAnularVenta, useCrearDevolucion } from '@/features/ventas/hooks'
import { useConfiguracion } from '@/features/configuracion/hooks'
import { TicketDocument } from './TicketPDF'
import { printTicket } from './printTicket'
import type { Venta, VentaEstado } from '@/types/database'
import { formatMoney, formatDate } from '@/lib/utils'
import { CloseIcon, HistoryIcon, AlertTriangleIcon, PrintIcon } from '@/components/shared/Icons'
import toast from 'react-hot-toast'

type Periodo = 'hoy' | 'ayer' | 'semana' | 'mes'

const PERIODOS: { key: Periodo; label: string }[] = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'ayer', label: 'Ayer' },
  { key: 'semana', label: 'Esta semana' },
  { key: 'mes', label: 'Este mes' },
]

const ESTADO_CONFIG: Record<VentaEstado, { label: string; pillClass: string }> = {
  pagada:         { label: 'Pagada',    pillClass: 'me-pill me-pill-ok' },
  pendiente_pago: { label: 'Pendiente', pillClass: 'me-pill me-pill-warn' },
  pago_parcial:   { label: 'Parcial',   pillClass: 'me-pill me-pill-warn' },
  anulada:        { label: 'Anulada',   pillClass: 'me-pill me-pill-neutral' },
}

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transf.',
  debito: 'Débito',
  credito: 'Crédito',
  cuenta_corriente: 'Cta. cte.',
}

function getPeriodoRango(periodo: Periodo): { desde: string; hasta: string } {
  const hoy = new Date()
  hoy.setHours(0, 0, 0, 0)
  const manana = new Date(hoy.getTime() + 86_400_000)

  switch (periodo) {
    case 'hoy':
      return { desde: hoy.toISOString(), hasta: manana.toISOString() }
    case 'ayer': {
      const ayer = new Date(hoy.getTime() - 86_400_000)
      return { desde: ayer.toISOString(), hasta: hoy.toISOString() }
    }
    case 'semana': {
      const inicioSemana = new Date(hoy.getTime() - 6 * 86_400_000)
      return { desde: inicioSemana.toISOString(), hasta: manana.toISOString() }
    }
    case 'mes': {
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      return { desde: inicioMes.toISOString(), hasta: manana.toISOString() }
    }
  }
}

function fmtHora(iso: string) {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
}

export default function HistorialVentasPage() {
  const { profile } = useAuth()
  const { data: depositos = [] } = useDepositos()
  const { data: cajaActual } = useCajaActual()
  const depositoId = depositos[0]?.id ?? ''

  const [periodo, setPeriodo] = useState<Periodo>('hoy')
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | 'minorista' | 'mayorista'>('todos')
  const [ventaDetalle, setVentaDetalle] = useState<Venta | null>(null)

  const { desde, hasta } = getPeriodoRango(periodo)
  const { data: ventas = [], isLoading } = useVentas({
    tipo: tipoFiltro !== 'todos' ? tipoFiltro : undefined,
    fechaDesde: desde,
    fechaHasta: hasta,
  })

  const totalRecaudado = ventas.reduce((s, v) => s + v.total_pagado, 0)
  const totalVentas = ventas.reduce((s, v) => s + v.total, 0)

  return (
    <div style={{ padding: 'var(--me-space-8)' }}>

      <h1 className="me-page-title" style={{ marginBottom: 'var(--me-space-6)' }}>
        Historial de ventas
      </h1>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--me-space-4)', gap: 'var(--me-space-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: 'var(--me-surface-2)', border: '1px solid var(--me-line)', borderRadius: 10, padding: 3, gap: 2 }}>
          {PERIODOS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriodo(p.key)}
              style={{
                padding: '5px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--me-sans)',
                transition: 'background var(--me-dur-short), color var(--me-dur-short)',
                background: periodo === p.key ? 'var(--me-surface)' : 'transparent',
                color: periodo === p.key ? 'var(--me-text)' : 'var(--me-text-3)',
                boxShadow: periodo === p.key ? 'var(--me-shadow-card)' : 'none',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {(['todos', 'minorista', 'mayorista'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTipoFiltro(t)}
              style={{
                padding: '5px 12px', borderRadius: 8, border: '1.5px solid',
                borderColor: tipoFiltro === t
                  ? t === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-acid)'
                  : 'var(--me-line)',
                background: tipoFiltro === t
                  ? t === 'mayorista' ? 'var(--me-mayor-soft)' : 'var(--me-acid-soft)'
                  : 'transparent',
                color: tipoFiltro === t
                  ? t === 'mayorista' ? 'var(--me-mayor)' : '#4a5800'
                  : 'var(--me-text-3)',
                fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'var(--me-sans)',
                transition: 'border-color var(--me-dur-short), background var(--me-dur-short)',
              }}
            >
              {t === 'todos' ? 'Todos' : t === 'minorista' ? 'Minorista' : 'Mayorista'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary bar */}
      {ventas.length > 0 && (
        <div style={{
          display: 'flex', gap: 'var(--me-space-6)', padding: '10px 16px',
          background: 'var(--me-surface)', border: '1px solid var(--me-line-2)',
          borderRadius: 10, marginBottom: 'var(--me-space-4)', flexWrap: 'wrap',
        }}>
          <div>
            <span className="me-eyebrow">Ventas</span>
            <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 18, fontWeight: 700, color: 'var(--me-text)' }}>
              {ventas.length}
            </div>
          </div>
          <div style={{ width: 1, background: 'var(--me-line-2)' }} />
          <div>
            <span className="me-eyebrow">Total facturado</span>
            <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 18, fontWeight: 700, color: 'var(--me-text)' }}>
              {formatMoney(totalVentas)}
            </div>
          </div>
          <div style={{ width: 1, background: 'var(--me-line-2)' }} />
          <div>
            <span className="me-eyebrow">Recaudado</span>
            <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 18, fontWeight: 700, color: 'var(--me-ok)' }}>
              {formatMoney(totalRecaudado)}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            Cargando...
          </div>
        ) : ventas.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <HistoryIcon size={28} style={{ color: 'var(--me-text-4)', marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--me-text-3)' }}>
              Sin ventas en este período — registrá una desde Nueva venta
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Cajero</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Items</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th>Método</th>
                </tr>
              </thead>
              <tbody>
                {ventas.map((venta, i) => {
                  const estado = ESTADO_CONFIG[venta.estado]
                  const itemCount = venta.venta_items?.reduce((s, it) => s + it.cantidad, 0) ?? 0
                  const metodo = venta.pagos?.[0] ? METODO_LABEL[venta.pagos[0].metodo] ?? venta.pagos[0].metodo : '—'

                  return (
                    <tr
                      key={venta.id}
                      onClick={() => setVentaDetalle(venta)}
                      style={{
                        cursor: 'pointer',
                        background: i % 2 === 1 ? 'var(--me-surface-2)' : 'transparent',
                        transition: 'background var(--me-dur-short)',
                        opacity: venta.estado === 'anulada' ? 0.5 : 1,
                      }}
                    >
                      <td>
                        <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 700 }}>
                          #{venta.numero}
                        </span>
                      </td>
                      <td style={{ color: 'var(--me-text-2)', fontSize: 13 }}>{formatDate(venta.created_at)}</td>
                      <td style={{ color: 'var(--me-text-3)', fontSize: 13, fontFamily: 'var(--me-mono)' }}>{fmtHora(venta.created_at)}</td>
                      <td style={{ fontSize: 12.5, color: 'var(--me-text-2)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {venta.profiles?.nombre ?? '—'}
                      </td>
                      <td>
                        {venta.tipo === 'mayorista'
                          ? <span className="me-pill me-pill-mayor">Mayorista</span>
                          : <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>Minorista</span>
                        }
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--me-text-3)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(venta.clientes as { nombre: string } | undefined)?.nombre ?? '—'}
                      </td>
                      <td><span className={estado.pillClass}>{estado.label}</span></td>
                      <td style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', color: 'var(--me-text-3)', fontSize: 13 }}>{itemCount}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{
                          fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 700,
                          color: venta.tipo === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)',
                        }}>
                          {formatMoney(venta.total)}
                        </span>
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--me-text-3)' }}>{metodo}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {ventaDetalle && (
        <VentaDetalleModal
          venta={ventaDetalle}
          depositoId={depositoId}
          usuarioId={profile?.id ?? ''}
          cajaActualId={cajaActual?.id ?? null}
          onClose={() => setVentaDetalle(null)}
          onAnulada={() => setVentaDetalle(null)}
        />
      )}
    </div>
  )
}

function VentaDetalleModal({
  venta, depositoId, usuarioId, cajaActualId, onClose, onAnulada,
}: {
  venta: Venta
  depositoId: string
  usuarioId: string
  cajaActualId: string | null
  onClose: () => void
  onAnulada: () => void
}) {
  const estado = ESTADO_CONFIG[venta.estado]
  const itemCount = venta.venta_items?.reduce((s, it) => s + it.cantidad, 0) ?? 0
  const [confirmAnular, setConfirmAnular] = useState(false)
  const [devolucionOpen, setDevolucionOpen] = useState(false)
  const [generandoPDF, setGenerandoPDF] = useState(false)
  const anularVenta = useAnularVenta()
  const { data: config } = useConfiguracion()

  // Pago no-CC (para reversión en caja)
  const pagoNonCC = venta.pagos?.find(p => p.metodo !== 'cuenta_corriente')

  async function handleReimprimir() {
    if (!config) { toast.error('Cargando configuración, intentá de nuevo'); return }
    try {
      await printTicket(venta, config)
    } catch {
      toast.error('Error al imprimir el ticket')
    }
  }

  async function handleDescargarPDF() {
    if (!config) { toast.error('Cargando configuración, intentá de nuevo'); return }
    setGenerandoPDF(true)
    try {
      const blob = await pdf(<TicketDocument venta={venta} config={config} />).toBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ticket-${venta.numero}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Error al generar el ticket')
    } finally {
      setGenerandoPDF(false)
    }
  }

  const pagoCC = venta.pagos?.find(p => p.metodo === 'cuenta_corriente')
  const tieneCC = !!pagoCC

  const handleAnular = () => {
    if (!venta.venta_items?.length) return
    const items = venta.venta_items.map(i => ({
      producto_id: (i as { producto_id: string }).producto_id,
      cantidad: i.cantidad,
    }))

    anularVenta.mutate(
      {
        ventaId: venta.id,
        numero: venta.numero,
        items,
        depositoId,
        usuarioId,
        tieneCC,
        clienteId: venta.cliente_id,
        totalCC: pagoCC?.monto,
        cajaActualId,
        metodoPago: pagoNonCC?.metodo,
        monto: pagoNonCC?.monto,
      },
      {
        onSuccess: () => {
          toast.success(`Venta #${venta.numero} anulada`)
          onAnulada()
        },
        onError: err => {
          toast.error(err instanceof Error ? err.message : 'Error al anular la venta')
          setConfirmAnular(false)
        },
      }
    )
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ boxShadow: 'var(--me-shadow-modal)', maxWidth: 520, width: '90vw' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--me-line-2)' }}>
          <div>
            <h2 className="me-section-title" style={{ margin: '0 0 4px' }}>
              Venta #{venta.numero}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>
                {formatDate(venta.created_at)} · {fmtHora(venta.created_at)}
                {venta.profiles?.nombre && ` · ${venta.profiles.nombre}`}
              </span>
              {venta.tipo === 'mayorista' && <span className="me-pill me-pill-mayor">Mayorista</span>}
              <span className={estado.pillClass}>{estado.label}</span>
              {(venta.clientes as { nombre: string } | undefined)?.nombre && (
                <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>
                  · {(venta.clientes as { nombre: string }).nombre}
                </span>
              )}
            </div>
          </div>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '55vh', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Items */}
          <div>
            <div className="me-eyebrow" style={{ marginBottom: 8 }}>Productos · {itemCount} unidades</div>
            <table className="me-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'center' }}>Cant.</th>
                  <th style={{ textAlign: 'right' }}>P. Unit.</th>
                  <th style={{ textAlign: 'center' }}>Desc.</th>
                  <th style={{ textAlign: 'right' }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(venta.venta_items ?? []).map((item, i) => (
                  <tr key={item.id} style={{ background: i % 2 === 1 ? 'var(--me-surface-2)' : 'transparent' }}>
                    <td style={{ fontSize: 13 }}>
                      {(item.productos as { nombre: string; marca?: string } | undefined)?.nombre ?? '—'}
                      {(item.productos as { marca?: string } | undefined)?.marca && (
                        <span style={{ color: 'var(--me-text-4)', marginLeft: 4, fontSize: 11.5 }}>
                          {(item.productos as { marca: string }).marca}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>{item.cantidad}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, color: 'var(--me-text-3)' }}>
                      {formatMoney(item.precio_unitario)}
                    </td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--me-mono)', fontSize: 12, color: item.descuento > 0 ? 'var(--me-ok)' : 'var(--me-text-4)' }}>
                      {item.descuento > 0 ? `${item.descuento}%` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, fontWeight: 600 }}>
                      {formatMoney(item.subtotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pago */}
          {venta.pagos && venta.pagos.length > 0 && (
            <div>
              <div className="me-eyebrow" style={{ marginBottom: 8 }}>Pago</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {venta.pagos.map(pago => (
                  <div key={pago.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--me-surface-2)', borderRadius: 8 }}>
                    <span style={{ fontSize: 13, color: 'var(--me-text-2)', fontWeight: 500 }}>
                      {METODO_LABEL[pago.metodo] ?? pago.metodo}
                    </span>
                    <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 14, fontWeight: 700 }}>
                      {formatMoney(pago.monto)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Descuento y total */}
          {venta.descuento_total > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--me-line-2)' }}>
              <span style={{ fontSize: 13, color: 'var(--me-ok)' }}>Descuento aplicado</span>
              <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, fontWeight: 600, color: 'var(--me-ok)' }}>
                -{formatMoney(venta.descuento_total)}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '2px solid var(--me-line)' }}>
            <span className="me-label">Total</span>
            <span style={{
              fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
              fontSize: 22, fontWeight: 800,
              color: venta.tipo === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)',
            }}>
              {formatMoney(venta.total)}
            </span>
          </div>

          {venta.notas && (
            <div style={{ padding: '10px 12px', background: 'var(--me-surface-2)', borderRadius: 8, fontSize: 13, color: 'var(--me-text-3)' }}>
              {venta.notas}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--me-line-2)' }}>
          {confirmAnular ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', background: 'var(--me-bad-soft)', borderRadius: 8,
                border: '1px solid rgba(239,68,68,0.2)',
              }}>
                <AlertTriangleIcon size={15} style={{ color: 'var(--me-bad)', flexShrink: 0 }} />
                <div style={{ fontSize: 12.5, color: 'var(--me-bad)' }}>
                  Se anulará la venta, se revertirá el stock
                  {tieneCC ? ' y se acreditará la cuenta corriente del cliente' : ''}.
                  Esta acción no se puede deshacer.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="me-btn me-btn-ghost"
                  onClick={() => setConfirmAnular(false)}
                  disabled={anularVenta.isPending}
                >
                  Cancelar
                </button>
                <button
                  className="me-btn me-btn-danger"
                  onClick={handleAnular}
                  disabled={anularVenta.isPending}
                >
                  {anularVenta.isPending ? 'Anulando...' : 'Confirmar anulación'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="me-btn me-btn-ghost"
                onClick={handleReimprimir}
                disabled={!config}
                style={{ display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <PrintIcon size={14} />
                Reimprimir
              </button>
              <button
                className="me-btn me-btn-ghost"
                onClick={handleDescargarPDF}
                disabled={generandoPDF}
              >
                {generandoPDF ? 'Generando...' : 'Descargar PDF'}
              </button>
              {venta.estado !== 'anulada' && (
                <button
                  className="me-btn me-btn-ghost"
                  onClick={() => setDevolucionOpen(true)}
                >
                  Devolución parcial
                </button>
              )}
              <div style={{ flex: 1 }} />
              {venta.estado !== 'anulada' && (
                <button
                  className="me-btn me-btn-danger"
                  onClick={() => setConfirmAnular(true)}
                >
                  Anular venta
                </button>
              )}
              <button className="me-btn me-btn-ghost" onClick={onClose}>Cerrar</button>
            </div>
          )}
        </div>
      </div>

      {devolucionOpen && (
        <DevolucionModal
          venta={venta}
          depositoId={depositoId}
          usuarioId={usuarioId}
          cajaActualId={cajaActualId}
          metodoPago={pagoNonCC?.metodo}
          onClose={() => setDevolucionOpen(false)}
          onConfirmada={() => { setDevolucionOpen(false); onAnulada() }}
        />
      )}
    </>
  )
}

function DevolucionModal({
  venta, depositoId, usuarioId, cajaActualId, metodoPago, onClose, onConfirmada,
}: {
  venta: Venta
  depositoId: string
  usuarioId: string
  cajaActualId: string | null | undefined
  metodoPago: string | undefined
  onClose: () => void
  onConfirmada: () => void
}) {
  const crearDevolucion = useCrearDevolucion()
  const items = venta.venta_items ?? []

  const [cantidades, setCantidades] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {}
    for (const item of items) init[item.id] = 0
    return init
  })
  const [motivo, setMotivo] = useState('')

  const pagoCC = venta.pagos?.find(p => p.metodo === 'cuenta_corriente')
  const tieneCC = !!pagoCC

  const total = items.reduce((s, item) => {
    const cant = cantidades[item.id] ?? 0
    const precioEfectivo = item.precio_unitario * (1 - item.descuento / 100)
    return s + precioEfectivo * cant
  }, 0)

  const hayItems = Object.values(cantidades).some(c => c > 0)

  async function handleConfirmar() {
    const itemsADevolver = items
      .filter(item => (cantidades[item.id] ?? 0) > 0)
      .map(item => ({
        venta_item_id: item.id,
        producto_id: (item as { producto_id: string }).producto_id,
        cantidad: cantidades[item.id],
        monto_unitario: item.precio_unitario * (1 - item.descuento / 100),
      }))
    try {
      const { numero } = await crearDevolucion.mutateAsync({
        ventaId: venta.id,
        items: itemsADevolver,
        motivo,
        usuarioId,
        depositoId,
        tieneCC,
        clienteId: venta.cliente_id,
        cajaActualId,
        metodoPago,
      })
      toast.success(`Devolución #${numero} registrada`)
      onConfirmada()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al registrar la devolución')
    }
  }

  return (
    <>
      <div
        className="me-overlay me-overlay-in"
        onClick={onClose}
        style={{ zIndex: 55 }}
      />
      <div
        className="me-modal me-modal-in"
        style={{ boxShadow: 'var(--me-shadow-modal)', maxWidth: 500, width: '90vw', zIndex: 56 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--me-line-2)' }}>
          <h2 className="me-section-title" style={{ margin: 0 }}>
            Devolución — Venta #{venta.numero}
          </h2>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '50vh', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="me-eyebrow" style={{ marginBottom: 10 }}>Seleccioná los ítems a devolver</div>
          {items.map(item => {
            const cant = cantidades[item.id] ?? 0
            const nombre = (item.productos as { nombre: string; marca?: string } | undefined)?.nombre ?? '—'
            const marca = (item.productos as { marca?: string } | undefined)?.marca
            return (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--me-line-2)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{nombre}</div>
                  {marca && <div style={{ fontSize: 11, color: 'var(--me-text-3)' }}>{marca}</div>}
                  <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 2 }}>
                    Vendido: {item.cantidad} u · {formatMoney(item.precio_unitario)} c/u
                  </div>
                </div>
                <input
                  className="me-input"
                  style={{ fontFamily: 'var(--me-mono)', textAlign: 'center', width: 72, padding: '5px 6px', fontSize: 13 }}
                  type="number"
                  min={0}
                  max={item.cantidad}
                  value={cant}
                  onChange={e => setCantidades(prev => ({
                    ...prev,
                    [item.id]: Math.min(item.cantidad, Math.max(0, parseInt(e.target.value) || 0)),
                  }))}
                />
              </div>
            )
          })}

          <div style={{ marginTop: 12 }}>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Motivo (opcional)</label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              type="text"
              placeholder="Ej: producto dañado, entrega incorrecta…"
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
            />
          </div>

          {tieneCC && hayItems && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8,
              padding: '10px 12px', borderRadius: 8,
              background: 'var(--me-warn-soft)', border: '1px solid rgba(245,158,11,0.25)',
            }}>
              <AlertTriangleIcon size={14} style={{ color: 'var(--me-warn)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: 'var(--me-text-2)' }}>
                Esta venta fue pagada en cuenta corriente. Se acreditarán {formatMoney(total)} en el saldo del cliente.
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--me-line-2)' }}>
          {hayItems && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span className="me-label">Total a devolver</span>
              <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 18, fontWeight: 700 }}>
                {formatMoney(total)}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="me-btn me-btn-ghost" onClick={onClose} disabled={crearDevolucion.isPending}>
              Cancelar
            </button>
            <button
              className="me-btn me-btn-primary"
              onClick={handleConfirmar}
              disabled={crearDevolucion.isPending || !hayItems}
            >
              {crearDevolucion.isPending ? 'Registrando…' : 'Confirmar devolución'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
